import { motion } from "framer-motion";

const cases = [
  {
    key: "kushals",
    client: "Kushal's Fashion Jewellery",
    title: "Heritage, Reimagined",
    palette:
      "radial-gradient(circle at 30% 20%, rgba(180,30,30,0.55), transparent 60%), linear-gradient(180deg, #2a0708 0%, #150404 100%)",
    body: "Kushal's carries pieces with history. The brief was simple: make them look like they belong in a glass case at a museum and on someone's feed.",
    body2:
      "We shot a close-up product campaign that leaned into texture, light, and the weight of the jewellery itself. Deep crimson satin, dramatic shadows, no distractions. Just the pieces, doing what they do.",
    deliverables: "Product reels + photography",
    accent: "#c22a2a",
    image:
      "https://customer-assets-jt897jd0.emergentagent.net/job_web-launch-501/artifacts/10ml1wb0__DSC8037.webp",
    video:
      "https://customer-assets-jt897jd0.emergentagent.net/job_web-launch-501/artifacts/42patfoc_jewel-2.mp4",
  },
  {
    key: "loccitane",
    client: "L'Occitane",
    title: "Slow Beauty",
    palette:
      "radial-gradient(circle at 70% 30%, rgba(217,144,54,0.5), transparent 60%), linear-gradient(180deg, #2a1a0b 0%, #140b06 100%)",
    body: "L'Occitane is a brand that asks you to slow down. So we did.",
    body2:
      "Close-up textures, warm light, botanicals in the background, a visual language that feels less like an ad and more like a Sunday morning. The kind of content that makes someone stop, exhale, and add to cart.",
    deliverables: "Product reels",
    accent: "#d99036",
    video:
      "https://customer-assets-jt897jd0.emergentagent.net/job_web-launch-501/artifacts/xce0q3vj_cosmetics.mp4",
  },
  {
    key: "cocacola",
    client: "Coca-Cola Zero Sugar",
    title: "Feel It Before You Drink It",
    palette:
      "radial-gradient(circle at 50% 50%, rgba(200,40,40,0.45), transparent 55%), linear-gradient(180deg, #1a0505 0%, #0a0303 100%)",
    body: "Everyone knows Coca-Cola. The challenge isn't awareness, it's sensation. Making someone feel the cold, the fizz, the pour before they've even touched the can.",
    body2:
      "We went macro. Condensation on red. Ice in darkness. Foam rising. Liquid spilling over glass. A campaign built entirely on texture and anticipation, no people, no lifestyle, no noise. Just the drink, doing its thing.",
    deliverables: "Product reels",
    accent: "#e63946",
    video:
      "https://customer-assets-jt897jd0.emergentagent.net/job_web-launch-501/artifacts/vy7sco2o_coke-full.mp4",
  },
];

const VisualPlaceholder = ({ palette, accent, index, image, video, client }) => (
  <div
    aria-label={image || video ? `${client} campaign visual` : "Visual TBD"}
    className="relative aspect-[4/5] w-full overflow-hidden border border-[#2a1f17]"
    style={{ background: palette }}
  >
    {video ? (
      <>
        <video
          src={video}
          poster={image}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, transparent 35%, rgba(10,7,5,0.55) 100%)",
          }}
        />
        <div
          className="absolute inset-0 mix-blend-multiply opacity-30"
          style={{ background: palette }}
        />
      </>
    ) : image ? (
      <>
        <img
          src={image}
          alt={`${client} — campaign still`}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, transparent 30%, rgba(10,7,5,0.55) 100%)",
          }}
        />
        <div className="absolute inset-0 mix-blend-multiply opacity-40" style={{ background: palette }} />
      </>
    ) : null}
    {/* concentric spotlight rings (only when no image/video) */}
    {!image && !video && (
      <div className="absolute inset-0 opacity-40">
        <div
          className="absolute inset-[10%] rounded-full border"
          style={{ borderColor: `${accent}55` }}
        />
        <div
          className="absolute inset-[20%] rounded-full border"
          style={{ borderColor: `${accent}33` }}
        />
        <div
          className="absolute inset-[30%] rounded-full border"
          style={{ borderColor: `${accent}22` }}
        />
      </div>
    )}
    {/* film ticks */}
    <div className="absolute inset-x-6 top-4 flex items-center justify-between font-body text-[10px] uppercase tracking-[0.28em] text-[#f9f6f0]/80">
      <span>◐ BHL / Studios</span>
      <span>Reel · 00{index}</span>
    </div>
    {/* corners */}
    {["top-4 left-4", "top-4 right-4", "bottom-4 left-4", "bottom-4 right-4"].map(
      (p) => (
        <span
          key={p}
          className={`absolute ${p} block h-4 w-4 border-[#f9f6f0]/40`}
          style={{ borderTop: "1px solid", borderLeft: "1px solid" }}
        />
      ),
    )}
    {/* center label */}
    {!image && !video && (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <span
          className="h-16 w-px"
          style={{ background: `${accent}aa` }}
          aria-hidden
        />
        <span className="font-serif-editorial text-2xl italic text-[#f9f6f0]">
          Visual&nbsp;TBD
        </span>
        <span className="overline text-[#f9f6f0]/70">Concept Campaign</span>
      </div>
    )}
    {/* bottom caption */}
    <div className="absolute inset-x-6 bottom-4 flex items-center justify-between font-body text-[10px] uppercase tracking-[0.28em] text-[#f9f6f0]/80">
      <span>Aspect 4:5</span>
      <span style={{ color: accent }}>● REC</span>
    </div>
  </div>
);

export default function Work() {
  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative bg-[#0a0705] py-32 md:py-48"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-16 flex flex-col items-end justify-between gap-6 md:mb-24 md:flex-row">
          <div>
            <p className="overline">
              <span className="text-[#d99036]">✦</span>&nbsp;&nbsp;The Work
            </p>
            <h2 className="mt-6 max-w-[900px] font-display text-6xl font-black leading-[0.9] tracking-tight md:text-8xl">
              Selected
              <br />
              <span className="font-serif-editorial italic font-normal text-[#d99036]">
                concept campaigns.
              </span>
            </h2>
          </div>
          <p className="max-w-sm font-body text-sm leading-relaxed text-[#a89f91] md:text-base">
            Three briefs, three worlds. All labelled concept campaigns — real assets landing soon.
          </p>
        </div>

        <div className="space-y-32 md:space-y-48">
          {cases.map((c, i) => (
            <motion.article
              key={c.key}
              data-testid={`case-${c.key}`}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className={`grid grid-cols-12 gap-8 md:gap-12 ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}
            >
              <div className="col-span-12 md:col-span-6 md:[direction:ltr]">
                <div className="sticky top-32">
                  <VisualPlaceholder
                    palette={c.palette}
                    accent={c.accent}
                    index={i + 1}
                    image={c.image}
                    video={c.video}
                    client={c.client}
                  />
                </div>
              </div>
              <div className="col-span-12 md:col-span-6 md:[direction:ltr]">
                <div className="flex flex-col justify-center gap-6 pt-4">
                  <div className="flex items-center gap-4">
                    <span
                      className="h-px w-12"
                      style={{ background: c.accent }}
                    />
                    <span className="overline text-[#f9f6f0]">
                      Case&nbsp;/&nbsp;0{i + 1} — Concept
                    </span>
                  </div>
                  <p className="font-body text-sm uppercase tracking-[0.24em] text-[#a89f91]">
                    {c.client}
                  </p>
                  <h3 className="font-display text-5xl font-black leading-[0.95] tracking-tight md:text-6xl">
                    <span className="font-serif-editorial italic font-normal">
                      {c.title.split(" ").slice(0, 1)}
                    </span>{" "}
                    {c.title.split(" ").slice(1).join(" ")}
                  </h3>
                  <p className="max-w-lg font-serif-editorial text-xl leading-relaxed text-[#f2ebdd] md:text-2xl">
                    {c.body}
                  </p>
                  <p className="max-w-lg font-body text-sm leading-relaxed text-[#a89f91] md:text-base">
                    {c.body2}
                  </p>
                  <div className="mt-4 flex items-center gap-4 border-t border-[#2a1f17] pt-6">
                    <span className="overline">Deliverables</span>
                    <span className="font-body text-sm text-[#f9f6f0]">
                      {c.deliverables}
                    </span>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
